/*
 * Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: MIT-0
 */
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap, map } from 'rxjs/operators';
import { Supplier } from './models/supplier.interface';

@Injectable({
  providedIn: 'root',
})
export class SupplierService {
  private suppliersCache: Map<string, Supplier[]> = new Map();
  private cacheTimestamp: Map<string, number> = new Map();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

  constructor(private http: HttpClient) {}
  baseUrl = `${localStorage.getItem('apiGatewayUrl')}`;

  // Read apiGatewayUrl at call time because this singleton is injected on pre-login screens
  // for cache clear before setTenantConfig writes the URL; construction-time baseUrl stays "null".
  // fetch/get/post/put/delete call this before each HTTP request.
  private resolveBaseUrl(): string {
    const url = localStorage.getItem('apiGatewayUrl');
    if (!url) {
      throw new Error('apiGatewayUrl is not configured');
    }
    this.baseUrl = url.replace(/\/$/, '');
    return this.baseUrl;
  }

  fetch(isActive?: string, tags?: string[], tagFilterCondition: 'OR' | 'AND' = 'OR'): Observable<Supplier[]> {
    const cacheKey = `${isActive || 'all'}_${tags ? tags.join(',') : ''}_${tagFilterCondition}`;
    
    // Check if we have cached data that's still valid
    if (this.isCacheValid(cacheKey)) {
      console.log('Suppliers fetched from cache');
      return of(this.suppliersCache.get(cacheKey)!);
    }

    // Cache miss or expired - fetch from API
    console.log('Suppliers fetched from API');
    const baseUrl = this.resolveBaseUrl();
    let url = `${baseUrl}/suppliers`;
    let params = new HttpParams();
    if (isActive) {
      params = params.set('isActive', isActive);
    }
    if (tags && tags.length > 0) {
      params = params.set('tags', tags.join(','));
      params = params.set('tagFilterCondition', tagFilterCondition);
    }
    
    return this.http.get<Supplier[]>(url, { params }).pipe(
      tap(suppliers => {
        // Cache the response
        this.suppliersCache.set(cacheKey, suppliers);
        this.cacheTimestamp.set(cacheKey, Date.now());
      })
    );
  }

  private isCacheValid(cacheKey: string): boolean {
    const cachedData = this.suppliersCache.get(cacheKey);
    const timestamp = this.cacheTimestamp.get(cacheKey);
    
    if (!cachedData || !timestamp) {
      return false;
    }
    
    return (Date.now() - timestamp) < this.CACHE_DURATION;
  }

  /**
   * Clear the suppliers cache - useful after creating, updating, or deleting suppliers
   */
  private clearCache(): void {
    this.suppliersCache.clear();
    this.cacheTimestamp.clear();
    console.log('Suppliers cache cleared');
  }

  /**
   * Manually clear the suppliers cache - useful for forcing a refresh
   */
  public clearSupplierCache(): void {
    this.clearCache();
  }
 
  get(supplierId: string): Observable<Supplier> {
    const url = `${this.resolveBaseUrl()}/supplier/${supplierId}`;
    return this.http.get<Supplier>(url);
  }

  delete(supplier: Supplier) {
    const url = `${this.resolveBaseUrl()}/supplier/${supplier.shardId}:${supplier.supplierId}`;
    return this.http.delete<Supplier>(url).pipe(
      tap(() => this.clearCache())
    );
  }

  put(supplier: Supplier) {
    const url = `${this.resolveBaseUrl()}/supplier/${supplier.shardId}:${supplier.supplierId}`;
    const body = JSON.parse(JSON.stringify({
      ...supplier,
      documentDescription: supplier.documentDescription ?? '',
    }));
    return this.http.put<Supplier>(url, body).pipe(
      tap(() => this.clearCache())
    );
  }

  post(supplier: Supplier) {
    return this.http.post<Supplier>(`${this.resolveBaseUrl()}/supplier`, supplier).pipe(
      tap(() => this.clearCache())
    );
  }
}
